import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Eye, Sparkles, TrendingUp, Activity, Target } from "lucide-react";
import { SectionHeader } from "./Services";

const INSIGHTS = [
    {
        key: "awareness",
        label: "Brand Awareness",
        icon: TrendingUp,
        before: 42,
        after: 71,
        unit: "%",
        story: "Aided awareness lifted by 29 points after the Q3 launch wave.",
    },
    {
        key: "csat",
        label: "Customer Satisfaction",
        icon: Sparkles,
        before: 6.4,
        after: 8.6,
        unit: "/10",
        story: "CSAT score climbed from 6.4 to 8.6 across 3,200 verified respondents.",
    },
    {
        key: "intent",
        label: "Purchase Intent",
        icon: Target,
        before: 18,
        after: 36,
        unit: "%",
        story: "Top-2-box purchase intent doubled in the under-35 segment.",
    },
];

export default function DataAlive() {
    const [active, setActive] = useState(INSIGHTS[0]);
    const ratio = (active.after / (active.unit === "/10" ? 10 : 100)) * 100;

    return (
        <section
            className="relative py-28"
            data-testid="data-alive-section"
        >
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />

            <div className="pr-container relative">
                <SectionHeader
                    overline="Interactive"
                    title="See how data comes alive"
                    subtitle="Hover over a metric to reveal the story. This is roughly what your insight portal will feel like."
                />

                <div className="mt-14 grid lg:grid-cols-5 gap-6">
                    {/* Left: metrics list */}
                    <div className="lg:col-span-2 flex flex-col gap-3">
                        {INSIGHTS.map((m, i) => {
                            const Icon = m.icon;
                            const selected = active.key === m.key;
                            return (
                                <motion.button
                                    key={m.key}
                                    onMouseEnter={() => setActive(m)}
                                    onFocus={() => setActive(m)}
                                    onClick={() => setActive(m)}
                                    initial={{ opacity: 0, x: -20 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.08 }}
                                    className={`text-left rounded-2xl p-5 flex items-center gap-4 transition-all border ${
                                        selected
                                            ? "bg-blue-500/10 border-blue-400/40 shadow-[0_0_30px_rgba(59,130,246,0.25)]"
                                            : "pr-card border-white/10 hover:border-blue-400/30"
                                    }`}
                                    data-testid={`insight-trigger-${m.key}`}
                                >
                                    <div
                                        className={`w-11 h-11 rounded-xl flex items-center justify-center ${
                                            selected
                                                ? "bg-emerald-500/15 border border-emerald-400/40"
                                                : "bg-white/5 border border-white/10"
                                        }`}
                                    >
                                        <Icon
                                            className={`w-5 h-5 ${
                                                selected
                                                    ? "text-emerald-300"
                                                    : "text-blue-300"
                                            }`}
                                        />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-outfit text-base text-white">
                                            {m.label}
                                        </p>
                                        <p className="text-xs font-mono-pr text-slate-400">
                                            Before {m.before}
                                            {m.unit} → After {m.after}
                                            {m.unit}
                                        </p>
                                    </div>
                                    <span
                                        className={`text-[10px] font-mono-pr uppercase tracking-widest ${
                                            selected
                                                ? "text-emerald-300"
                                                : "text-slate-500"
                                        }`}
                                    >
                                        {selected ? "Active" : "Hover"}
                                    </span>
                                </motion.button>
                            );
                        })}
                    </div>

                    {/* Right: visualization */}
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="lg:col-span-3 pr-glass-strong rounded-3xl p-6 sm:p-8 relative overflow-hidden"
                        data-testid="insight-visual-card"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-xs font-mono-pr text-slate-400">
                                <Eye className="w-4 h-4 text-blue-400" />
                                live insight viewer
                            </div>
                            <div className="flex items-center gap-2 text-xs font-mono-pr text-emerald-300">
                                <Activity className="w-3.5 h-3.5" />
                                signal · stable
                            </div>
                        </div>

                        <AnimatePresence mode="wait">
                            <motion.div
                                key={active.key}
                                initial={{ opacity: 0, y: 18 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -10 }}
                                transition={{ duration: 0.35 }}
                                className="mt-6"
                            >
                                <p className="font-outfit text-xs uppercase tracking-[0.25em] text-blue-400">
                                    {active.label}
                                </p>
                                <div className="mt-1 flex items-baseline gap-3">
                                    <span className="font-outfit text-6xl font-black text-white text-glow-blue">
                                        {active.after}
                                        {active.unit}
                                    </span>
                                    <span className="text-sm text-emerald-300 font-mono-pr">
                                        ▲ from {active.before}
                                        {active.unit}
                                    </span>
                                </div>
                                <p className="mt-3 text-slate-300 max-w-md leading-relaxed">
                                    {active.story}
                                </p>

                                {/* Progress bars */}
                                <div className="mt-6 space-y-3">
                                    <BarRow
                                        label="Before"
                                        value={
                                            active.unit === "/10"
                                                ? (active.before / 10) * 100
                                                : active.before
                                        }
                                        color="from-slate-500 to-slate-400"
                                    />
                                    <BarRow
                                        label="After"
                                        value={ratio}
                                        color="from-blue-500 to-emerald-400"
                                        glow
                                    />
                                </div>

                                {/* Animated bars */}
                                <div className="mt-7 grid grid-cols-12 gap-1.5 h-24">
                                    {Array.from({ length: 12 }).map((_, i) => {
                                        const h = 30 + ((i * 13 + active.after) % 60);
                                        return (
                                            <motion.div
                                                key={i}
                                                animate={{ height: `${h}%` }}
                                                transition={{
                                                    duration: 0.7,
                                                    delay: i * 0.04,
                                                }}
                                                className="self-end rounded-md bg-gradient-to-t from-blue-600/30 to-blue-400/80"
                                            />
                                        );
                                    })}
                                </div>
                            </motion.div>
                        </AnimatePresence>
                    </motion.div>
                </div>
            </div>
        </section>
    );
}

function BarRow({ label, value, color, glow }) {
    return (
        <div>
            <div className="flex items-center justify-between text-xs font-mono-pr text-slate-400">
                <span>{label}</span>
                <span>{Math.round(value)}%</span>
            </div>
            <div className="mt-1.5 h-2 rounded-full bg-white/5 overflow-hidden border border-white/5">
                <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${value}%` }}
                    transition={{ duration: 1.2, ease: "easeOut" }}
                    className={`h-full bg-gradient-to-r ${color} ${
                        glow ? "shadow-[0_0_18px_rgba(59,130,246,0.6)]" : ""
                    }`}
                />
            </div>
        </div>
    );
}
